/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useEffect, useState } from 'react';
import Spell from './model/Spell';
import SpellLevelsSelector from './character/SpellLevelsSelector';
import Class from './model/Class';
import ClassService from './services/ClassService';
import SpellService from './services/SpellService';
import { ClassDetails } from './model/ClassDetails';
import NpcData from './model/NpcData';
import Attributes from './model/Attributes';
import AttributeValues from './model/AttributeValues';

interface SpellcastingSelectorProps {
  isOpen: boolean;
  ability: Ability;
  attributes: AttributeValues;
  npcData: NpcData | null;
  onSelect: (classInfo1: ClassDetails | null, classInfo2: ClassDetails | null,
             spellSlots: number[], warlockSpellSlots: number[] | null,
             npcData: NpcData | null) => void;
  onNpcUpdate: (npcData: NpcData) => void;
  onRefreshTour: (tourState: string) => void;
  onCancel: () => void;
  onClickOutside: (event: MouseEvent) => void;
  openLevel: number;
  setOpenLevel: (newValue: number) => void;
}

const SpellcastingSelector: React.FC<SpellcastingSelectorProps> = ({ isOpen, ability, attributes, npcData, onSelect, onNpcUpdate, onCancel, onRefreshTour, onClickOutside, openLevel, setOpenLevel }) => {
  const [primaryClassDetails,       setPrimaryClassDetails]       = useState<ClassDetails | null>(null);
  const [secondaryClassDetails,     setSecondaryClassDetails]     = useState<ClassDetails | null>(null);
  const [castingClassesByName,      setCastingClassesByName]      = useState<Map<string, Class>>(new Map());
  const [classesByName,             setClassesByName]             = useState<Map<string, Class>>(new Map());
  const [spellsBySpellLevelByClass, setSpellsBySpellLevelByClass] = useState<Map<string, Map<number, Spell[]>>>(new Map());
  const [spellsByKey,               setSpellsByKey]               = useState<Map<string, Spell>>(new Map());
  const [includeSpellDetails,       setIncludeSpellDetails]       = useState<boolean>(false);

  useEffect(() => {
      // Define the function inside useEffect
      const fetchSpellData = async () => {
        try {
          const classData = await ClassService.fetchClasses(false);
          const spells = await SpellService.fetchSpellsBySpellLevelByClass();

          setSpellsBySpellLevelByClass(spells);

          const casterClassesByName = new Map();
          for (const classInfo of classData) {
            classesByName.set(classInfo.name.toLowerCase(), classInfo);
            if ((classInfo.base == null) && ClassService.getSpellsKnownForClassLevel(classInfo, 20, attributes) > 0) {
              casterClassesByName.set(classInfo.name.toLowerCase(), classInfo);
            }
          }
          for (const classInfo of classData) {
            if (classInfo.base != null) {
              if (ClassService.getSpellsKnownForClassLevel(classInfo, 20, attributes) > 0) {
                if (!casterClassesByName.has(classInfo.base.name.toLowerCase())) {
                  casterClassesByName.set(classInfo.name.toLowerCase(), classInfo);
                }
              }
            }
          }

          const newSpellsByKey = new Map<string, Spell>();
          for (const clss of spells.keys()) {
            const classSpells = spells.get(clss);
            for (const level of classSpells?.keys() || []) {
              const levelSpells  = classSpells?.get(level);
              for (const spell of levelSpells || []) {
                newSpellsByKey.set(spell.key, spell);
              }
            }
          }
          setSpellsByKey(newSpellsByKey);
  
          casterClassesByName.set(ClassService.metaCaster.name.toLowerCase(), ClassService.metaCaster);
          classesByName.set(ClassService.metaCaster.name.toLowerCase(), ClassService.metaCaster);
          
          setCastingClassesByName(casterClassesByName);
          setClassesByName(classesByName);
  
        } catch (error) {
          console.error('Error fetching data:', error);
        }
      };
  
      // Call the function
      fetchSpellData();
    }, []);

    const getClassDetails = (className: string | null, archetypeName: string, level: number,
                             spellbookSpellKeysBySpellLevelPrimary: string[],
                             spellKeysPreparedBySpellLevelPrimary: string[]): ClassDetails | null => {
      if (!className) {
        return null;
      }
      let classInfo = classesByName.get(className.toLowerCase());
      if (classInfo == null) {
        // Create a dummy, because this would block a user adding a spellcasting block before all the class data has loaded,
        // which in-turn prevents a new user from completing the Tour
        classInfo = {
          name: className,
          desc: 'temp',
          slug: className,
          subtypes_name: '',
          hit_dice: 'd8',
          prof_armor: '',
          prof_weapons: '',
          prof_tools: '',
          prof_saving_throws: '',
          prof_skills: '',
          equipment: '',
          spellcasting_ability: 'int',
          table: '',
          sections: [],
          getAsiLevels: (level: number) => [],
          document__slug: '',
          document__title: '',
          document__license_url: '',
          document__url: ''
        }
      }
      const archetype = (archetypeName && classesByName.get(archetypeName.toLowerCase())) || null;
      if (classInfo && (castingClassesByName.get(className.toLowerCase()) || castingClassesByName.get(archetypeName.toLowerCase()))) {
        const classDetails = new ClassDetails();
        classDetails.classInfo = classInfo;
        classDetails.archetype = archetype;
        classDetails.classLevel = level || (classInfo ? 1 : 0);  // if classInfo is present, classLevel is at least '1'.
        classDetails.spellbookSpellsArrayBySpellLevel = NpcData.getSpellsFromKeys(spellbookSpellKeysBySpellLevelPrimary || [], spellsByKey);
        classDetails.spellsPreparedArrayBySpellLevel  = NpcData.getSpellsFromKeys(spellKeysPreparedBySpellLevelPrimary || [], spellsByKey);
        classDetails.validateSpells(spellsBySpellLevelByClass);
        return classDetails;
      }
      return null;
    }

    const updateClasses = () => {
      if (npcData) {
        const newPrimary = getClassDetails(npcData.primaryClassName,
                                           npcData.primaryArchetypeName,
                                           npcData.primaryLevel,
                                           npcData.spellbookSpellKeysBySpellLevelPrimary,
                                           npcData.spellKeysPreparedBySpellLevelPrimary);
        setPrimaryClassDetails(newPrimary);
        const newSecondary = getClassDetails(npcData.secondaryClassName,
                                             npcData.secondaryArchetypeName,
                                             npcData.secondaryLevel,
                                             npcData.spellbookSpellKeysBySpellLevelSecondary,
                                             npcData.spellKeysPreparedBySpellLevelSecondary);
        setSecondaryClassDetails(newSecondary);
      }
    }

    useEffect(() => {
      if (spellsBySpellLevelByClass.size == 0) {
        return;
      }
      let spellsCount = 0;
      for (const slugsByLevel of npcData?.spellKeysPreparedBySpellLevelPrimary || []) {
        spellsCount += slugsByLevel.split(',').length;
      }
      if (!npcData || !npcData.primaryClassName || npcData.primaryClassName.toLowerCase().startsWith("inate caster") || spellsCount == 0) {
        const spellsByLevel: Spell[][] = [[],[],[],[],[],[],[],[],[],[]];
        let level = 0;
        let attribute = ability.attribute || '';
        let className = npcData?.primaryClassName || '';
        let caster = false;
        for (const line of ability.desc.split('\n')) {
          const levelMatch = line.match(/(\d+)[fst][tdh][ -]level spellcaster/i);
          if (levelMatch) {
            level = parseInt(levelMatch[1]);
          }
          const attributeMatch = line.match(/ spellcasting ability is ([^ ]*) /i);
          if (attributeMatch) {
            attribute = Attributes.getAttrName(attributeMatch[1]);
          }
          const classMatch = line.match(/ following ([a-z]*) spells/i);
          if (classMatch) {
            if (spellsBySpellLevelByClass.has(classMatch[1].toLowerCase())) {
              className = classMatch[1];
            }
          }
          if (line.startsWith('|') && line.endsWith('|')) {
            const cells: string[] = line.split('|');
            const fullSpellSet = spellsBySpellLevelByClass.get('inate caster') || new Map();
            if (cells.length >= 4) {
              if (cells[1].trim().toLowerCase() == 'level' && cells[2].trim().toLowerCase() == 'slots') {
                const levelMatch = line.match(/.*-(\d+) spells/i);
                if (levelMatch) {
                  level = parseInt(levelMatch[1]);
                }
              }
              // This specifies the order of the document__slugs t check for first:
              const slugOrder = ['wotc-srd', ''];
              for (let spellLevel = 0 ; spellLevel <= 9 ; spellLevel++) {
                if ((spellLevel === 0 && cells[1].trim().toLowerCase().startsWith('cantrip')) ||
                    cells[1].trim().toLowerCase() === ClassService.getLevelString(spellLevel)) {
                  const levelSpells: Spell[] = fullSpellSet.get(spellLevel) || [];
                  const spells: string = cells[3].trim();
                  const spellsArray = spells.split(",");
                  for (const spll of spellsArray) {
                    const spellName = spll.trim().toLowerCase().replace(/[_\*]/g, ' ').replace(/\(ᚱ\)/, '').trim();
                    let found = false;
                    let spellMatchingName = levelSpells.filter((spell) => spell.name.toLowerCase() === spellName);
                    mainLoop:
                    for (const slug of slugOrder) {
                      for (const spell of spellMatchingName) {
                        if (!slug || spell.document.key === slug) {
                          spellsByLevel[spellLevel].push(spell);
                          found = true;
                          caster = true;
                          break mainLoop;
                        }
                      }
                    }
                    if (!found) {
                      // Look for this spell at a lower level
                      for (let altLevel = spellLevel-1 ; altLevel >= 0 ; altLevel--) {
                        const levelSpells: Spell[] = fullSpellSet.get(altLevel) || [];
                        spellMatchingName = levelSpells.filter((spell) => spell.name.toLowerCase() === spellName);
                        secondLoop:
                        for (const slug of slugOrder) {
                          for (const spell of spellMatchingName) {
                            if (!slug || spell.document.key === slug) {
                              spellsByLevel[altLevel].push(spell);
                              found = true;
                              caster = true;
                              break secondLoop;
                            }
                          }
                        }
                      }
                    }
                  }
                  break;
                }
              }
            }
          }
        }
        if (caster) {
          const newNpcData = structuredClone(npcData) || new NpcData();
          if (!className) {
            if (!attribute) {
              attribute = 'wisdom';
            }
            className = "Inate Caster";
          }
          newNpcData.primaryClassName = className;
          newNpcData.primaryLevel = level;
          if (attribute) {
            newNpcData.primaryAttribute = attribute;
          }
          newNpcData.spellsPreparedArrayBySpellLevelPrimary = spellsByLevel;
          newNpcData.spellKeysPreparedBySpellLevelPrimary = NpcData.getKeysFromSpells(spellsByLevel);
          if (className.toLowerCase() === 'wizard') {
            newNpcData.spellbookSpellsArrayBySpellLevelPrimary = spellsByLevel;
            newNpcData.spellbookSpellKeysBySpellLevelPrimary = newNpcData.spellKeysPreparedBySpellLevelPrimary;
          }
          onNpcUpdate(newNpcData);
        }
      }
    }, [ability, spellsBySpellLevelByClass]);

    useEffect(() => {
      updateClasses();
    }, [npcData, castingClassesByName, classesByName]);

  const handleSelect = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const spellSlots: number[] = SpellService.getSpellSlots(primaryClassDetails, secondaryClassDetails);
    const warlockSpellSlots: number[] | null = SpellService.getWarlockSpellSlots(primaryClassDetails?.classInfo || null, primaryClassDetails?.classLevel || 0) ||
                                               SpellService.getWarlockSpellSlots(secondaryClassDetails?.classInfo || null, secondaryClassDetails?.classLevel || 0);
    onSelect(primaryClassDetails, secondaryClassDetails, spellSlots, warlockSpellSlots, npcData);
  };

  const handleCancel = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onCancel();
  };

  if (!isOpen) {
    return null;
  }

  const handleSpellbookChanged = (level: number, spellsSelected: Spell[], classDetails: ClassDetails | null) => {
    if (classDetails) {
      const newSpellbookSpellsArrayBySpellLevel = [...classDetails.spellbookSpellsArrayBySpellLevel];
      newSpellbookSpellsArrayBySpellLevel[level] = spellsSelected;
      classDetails.spellbookSpellsArrayBySpellLevel = newSpellbookSpellsArrayBySpellLevel;
      if (npcData) {
        const className = classDetails?.classInfo?.name.toLowerCase();
        const newNpcData = structuredClone(npcData);
        if (newNpcData.primaryClassName.toLowerCase() == className || newNpcData.primaryArchetypeName.toLowerCase() == className) {
          newNpcData.spellbookSpellKeysBySpellLevelPrimary = NpcData.getKeysFromSpells(newSpellbookSpellsArrayBySpellLevel);
          onNpcUpdate(newNpcData);
        } else if (newNpcData.secondaryClassName.toLowerCase() == className || newNpcData.secondaryArchetypeName.toLowerCase() == className) {
          newNpcData.spellbookSpellKeysBySpellLevelSecondary = NpcData.getKeysFromSpells(newSpellbookSpellsArrayBySpellLevel);
          onNpcUpdate(newNpcData);
        }
      }
    }
  }

  const handleSpellsSelectedChanged = (level: number, spellsSelected: Spell[], classDetails: ClassDetails | null) => {
    if (classDetails) {
      const newSpellsPreparedArrayBySpellLevel = [...classDetails.spellsPreparedArrayBySpellLevel];
      newSpellsPreparedArrayBySpellLevel[level] = spellsSelected;
      classDetails.spellsPreparedArrayBySpellLevel = newSpellsPreparedArrayBySpellLevel;
      if (npcData) {
        const className = classDetails?.classInfo?.name.toLowerCase();
        const newNpcData = structuredClone(npcData);
        if (newNpcData.primaryClassName.toLowerCase() == className || newNpcData.primaryArchetypeName.toLowerCase() == className) {
          newNpcData.spellKeysPreparedBySpellLevelPrimary = NpcData.getKeysFromSpells(newSpellsPreparedArrayBySpellLevel);
          onNpcUpdate(newNpcData);
        } else if (newNpcData.secondaryClassName.toLowerCase() == className || newNpcData.secondaryArchetypeName.toLowerCase() == className) {
          newNpcData.spellKeysPreparedBySpellLevelSecondary = NpcData.getKeysFromSpells(newSpellsPreparedArrayBySpellLevel);
          onNpcUpdate(newNpcData);
        }
      }
    }
  }

  const handleSetClassDetails = (className: string, classLevel: number, isPrimary: boolean) => {
    const newNpcData = structuredClone(npcData) || new NpcData();
    // Look up the new class's default spellcasting attribute so it auto-syncs with the class change.
    const newClassInfo = className ? classesByName.get(className.toLowerCase()) : null;
    const defaultAttr = (newClassInfo ? (ClassService.getSpellCastingAttributeByClassName(newClassInfo.name) || '') : '').toLowerCase();
    // For classes with a built-in spellcasting attribute (Wizard, Cleric...), snap to that default.
    // For 'Inate Caster' (no class default), keep whatever was previously set, falling back to charisma.
    if (isPrimary) {
      newNpcData.primaryClassName = className;
      newNpcData.primaryLevel = classLevel || (className ? 1 : 0);
      newNpcData.primaryAttribute = defaultAttr || newNpcData.primaryAttribute || 'charisma';
    } else {
      newNpcData.secondaryClassName = className;
      newNpcData.secondaryLevel = classLevel || (className ? 1 : 0);
      newNpcData.secondaryAttribute = defaultAttr || newNpcData.secondaryAttribute || 'charisma';
    }
    onNpcUpdate(newNpcData);
    onRefreshTour("SpellCastingSelector: " + className + '|' + classLevel + '|' + isPrimary);
    updateClasses();
  }

  const handleAttributeChanged = (attribute: string, isPrimary: boolean) => {
    if (!npcData) return;
    const newNpcData = structuredClone(npcData);
    if (isPrimary) {
      newNpcData.primaryAttribute = attribute;
    } else {
      newNpcData.secondaryAttribute = attribute;
    }
    onNpcUpdate(newNpcData);
  }

  const onSetOpenLevel = (level: number) => {
    setOpenLevel(level);
    onRefreshTour("SpellCastingSelector: " + npcData?.primaryClassName + '|' + npcData?.primaryLevel + '|Spellbook:' + level);
  }
  return (
    <div className="ability-container edit-field spellcasting-selector">
      <div className="multi-select-panel ability-panel">
      <SpellLevelsSelector primaryClassDetails={primaryClassDetails}
                           secondaryClassDetails={secondaryClassDetails}
                           attributeValues={attributes} spellsBySpellLevelByClass={spellsBySpellLevelByClass}
                           primaryAttribute={(npcData?.primaryAttribute || (primaryClassDetails ? ClassService.getSpellCastingAttribute(primaryClassDetails) : '')).toLowerCase()}
                           secondaryAttribute={(npcData?.secondaryAttribute || (secondaryClassDetails ? ClassService.getSpellCastingAttribute(secondaryClassDetails) : '')).toLowerCase()}
                           onPrimaryAttributeChanged={(attribute) => handleAttributeChanged(attribute, true)}
                           onSecondaryAttributeChanged={(attribute) => handleAttributeChanged(attribute, false)}
                           onSpellbookSpellsPrimaryChanged={(level, spellNames) => handleSpellbookChanged(level, spellNames, primaryClassDetails)}
                           onSpellsPreparedPrimaryChanged={(level, spellNames) => handleSpellsSelectedChanged(level, spellNames, primaryClassDetails)}
                           onSpellbookSpellsSecondaryChanged={(level, spellNames) => handleSpellbookChanged(level, spellNames, secondaryClassDetails)}
                           onSpellsPreparedSecondaryChanged={(level, spellNames) => handleSpellsSelectedChanged(level, spellNames, secondaryClassDetails)}
                           openLevel={openLevel}
                           setOpenLevel={onSetOpenLevel}
                           setClassDetails={handleSetClassDetails}
                           onClickOutside={onClickOutside}
                          />
      {/* <label>
        <input type='checkbox' checked={includeSpellDetails} onChange={() => setIncludeSpellDetails((oldVal) => !oldVal)} />
        Include spell details
      </label><br/> */}
        <button onClick={handleSelect}>Select</button>
        <button onClick={handleCancel}>Cancel</button>
      </div>
    </div>
  );
};

export default SpellcastingSelector;
